import React, { useEffect, useId, useMemo, useRef } from "react";
import { Map, Marker, useMap } from "@vis.gl/react-google-maps";
import { useThemeMode } from "context/ThemeModeContext";
import { GOOGLE_MAP_DARK_STYLES } from "styles/maps/googleMapDarkStyles";

export interface Markers {
  lat: number;
  lng: number;
  label: string;
  onClick?: () => void;
  icon?: google.maps.Symbol | google.maps.Icon;
}

interface GoogleMapBoxProps {
  address: string;
  latitude: number;
  longitude: number;
  draggable?: boolean;
  onAddressUpdate?: (
    address: string,
    coordinates: { lat: number; lng: number },
  ) => void;
  onDragEnd?: (coordinates: { lat: number; lng: number }) => void;
  markers?: Markers[];
  onMapLoad?: (map: google.maps.Map) => void;
  setMapRef?: (map: google.maps.Map) => void;
  fitBounds?: boolean;
  zoom?: number;
}

function MapInstanceBinder({
  mapId,
  markers,
  fitBounds,
  zoom,
  onMapLoad,
  setMapRef,
  styles,
}: {
  mapId: string;
  markers?: Markers[];
  fitBounds: boolean;
  zoom: number;
  onMapLoad?: (map: google.maps.Map) => void;
  setMapRef?: (map: google.maps.Map) => void;
  styles: google.maps.MapTypeStyle[] | null;
}) {
  const map = useMap(mapId);
  const didInitRef = useRef(false);

  useEffect(() => {
    if (!map || didInitRef.current) return;
    didInitRef.current = true;
    setMapRef?.(map);
    onMapLoad?.(map);

    if (markers && markers.length > 0 && fitBounds) {
      fitMapToMarkers(map, markers, zoom);
    } else if (zoom) {
      map.setZoom(zoom);
    }
  }, [map, markers, fitBounds, zoom, onMapLoad, setMapRef]);

  useEffect(() => {
    if (!map) return;
    map.setOptions({ styles: styles ?? [] });
  }, [map, styles]);

  return null;
}

function GoogleMapBox({
  latitude,
  longitude,
  markers,
  onMapLoad,
  setMapRef,
  fitBounds = true,
  zoom = 15,
}: GoogleMapBoxProps) {
  const { resolved } = useThemeMode();
  const reactId = useId();
  const mapId = useMemo(
    () => `map-${reactId.replace(/:/g, "")}`,
    [reactId],
  );
  const styles = resolved === "dark" ? GOOGLE_MAP_DARK_STYLES : null;

  return (
    <Map
      id={mapId}
      key={resolved}
      cameraControl
      style={{ width: "100%", height: "100%" }}
      defaultCenter={{ lat: latitude, lng: longitude }}
      defaultZoom={zoom}
      gestureHandling="greedy"
      disableDefaultUI
      styles={styles ?? []}
    >
      <MapInstanceBinder
        mapId={mapId}
        markers={markers}
        fitBounds={fitBounds}
        zoom={zoom}
        onMapLoad={onMapLoad}
        setMapRef={setMapRef}
        styles={styles}
      />
      {markers?.map((marker) => (
        <Marker
          key={`${marker.lat}-${marker.lng}-${marker.label}`}
          position={{ lat: marker.lat, lng: marker.lng }}
          onClick={marker.onClick}
          icon={marker.icon}
          title={marker.label}
        />
      ))}
    </Map>
  );
}

function fitMapToMarkers(
  map: google.maps.Map,
  markers: Markers[],
  zoom?: number,
) {
  const bounds = new google.maps.LatLngBounds();
  markers.forEach((marker) => {
    bounds.extend({ lat: marker.lat, lng: marker.lng });
  });

  if (markers.length === 1) {
    map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
    map.setZoom(zoom || 15);
    return;
  }

  map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
}

export default React.memo(GoogleMapBox);
